/**
 * The collapsed motion column view (W2-04a motion-columns; DESIGN.md §4.7, §5.6, ARCHITECTURE.md §7.7): one
 * `TimelineColumnView` per motion column instance (one property of one line on one layer, the Guided pairs, and
 * pinned columns of other lines). The feature layer (src/features/motion) creates it with:
 *
 *  - `scene(axis, width)`: the prepared, translated scene (keys, sampled curves, badges...). Called for every content
 *    draw and every hit-test; the feature caches it, so repeated calls are cheap;
 *  - `overlay(axis, width)`: live feedback for the overlay layer (hover ring, placement and paste ghosts, drag
 *    readout, value at the playhead), redrawn every frame while active;
 *  - `handlers`: pointer and wheel behaviour (selection, drags as live gestures, double-click to add a key, the
 *    context menu, RPE Ctrl+wheel fine-tuning, value zoom and pan through the host's `zoomX` / `panX`). The view
 *    hit-tests and forwards; it owns no editing logic.
 *  - `decorateElement`: called with the host's element of the column on the first draw and on every content draw
 *    (it must be idempotent), so the feature can give it its focus / hover region, context menu attributes
 *    (data-region="motion"), which the keyboard router and the context menu host read (see
 *    docs/design/change-requests/W2-04a.md), and keep its accessible name current.
 *
 * The view keeps the client rectangle of its canvas (for context menus at the pointer) and the last scene, and
 * answers the host's marquee (`itemsInRect`) from the same geometry it draws.
 */

import type { LineId } from '../../../core/model/types';
import type { DirtyScopes, Op } from '../../../core/ops/types';
import type {
  TimeAxis,
  TimelineColumnView,
  TimelineHit,
  TimelineLayer,
  TimelinePointerEvent,
  TimelineRectItems,
} from '../../types';
import { axisContext, isStripAxis } from '../axis';
import type { DocScopeView } from '../docChange';
import { drawMotionContent, drawMotionFixed, drawMotionOverlay, drawMotionStatic, themeFor } from './motionDraw';
import { hitTestColumn, keysInRect, laneRects, type LaneRect } from './motionHit';
import type { MotionColumnScene, MotionHit, MotionOverlayScene } from './motionScene';

export interface MotionColumnHandlers {
  pointerDown?(view: MotionColumnView, e: TimelinePointerEvent, hit: MotionHit | null): void;
  /** Moves of a press, and hover moves (buttons 0). */
  pointerMove?(view: MotionColumnView, e: TimelinePointerEvent): void;
  pointerUp?(view: MotionColumnView, e: TimelinePointerEvent): void;
  doubleClick?(view: MotionColumnView, e: TimelinePointerEvent, hit: MotionHit | null): void;
  /** `client` = the pointer in client coordinates (for placing a context menu), null when unknown. */
  contextMenu?(
    view: MotionColumnView,
    e: TimelinePointerEvent,
    hit: MotionHit | null,
    client: { readonly x: number; readonly y: number } | null,
  ): void;
  /** Wheel over the column; true when consumed (otherwise the host scrolls / zooms). */
  wheel?(view: MotionColumnView, e: WheelEvent, axis: TimeAxis): boolean;
  /** Ctrl+Shift+wheel over the column: zoom the value axis by `factor` (> 1 = in) about column x. */
  zoomX?(view: MotionColumnView, factor: number, x: number): void;
  /** Horizontal wheel or middle drag: pan the value axis by `dxPx` (positive = the view moves right). */
  panX?(view: MotionColumnView, dxPx: number): void;
  pointerLeave?(view: MotionColumnView): void;
  pointerCancel?(view: MotionColumnView): void;
  disposed?(view: MotionColumnView): void;
}

export interface MotionColumnDeps {
  /** The column instance id (`motion:<line>:<prop>:<layer>`). */
  readonly id: string;
  scene(axis: TimeAxis, widthPx: number): MotionColumnScene;
  overlay(axis: TimeAxis, widthPx: number): MotionOverlayScene | null;
  readonly handlers: MotionColumnHandlers;
  decorateElement?(el: HTMLElement): void;
}

/** Line of a motion column instance id (`motion:<line>:<prop>:<layer>`), or null when the id is not one. */
export function lineOfMotionId(id: string): LineId | null {
  const m = /^motion:(\d+):/.exec(id);
  return m === null ? null : (Number(m[1]) as LineId);
}

/**
 * The view plus what the feature layer asks of it during gestures. It opts into the host's scroll cache
 * (TimelineHost `ScrollCacheView`): static and content are placed by beats only, the parts pinned to the visible area
 * are drawn by `drawFixed`.
 */
export interface MotionColumnView extends TimelineColumnView, DocScopeView {
  readonly scrollCache: true;
  drawFixed(ctx: CanvasRenderingContext2D, axis: TimeAxis, widthPx: number): void;
  readonly id: string;
  /** Current width (CSS px). */
  readonly width: number;
  /** The axis of the last draw or pointer event (null before either). */
  readonly axis: TimeAxis | null;
  /** The scene now (from the feature's cache). */
  scene(): MotionColumnScene | null;
  /** Lane rectangles and value maps of the current scene. */
  lanes(): readonly LaneRect[];
  /** Hit-test at column coordinates with the current axis. */
  hit(x: number, y: number): MotionHit | null;
  /** The host's element of the column (null until the first draw). */
  element(): HTMLElement | null;
  /** Client coordinates of a column-local point (null until the first draw). */
  toClient(x: number, y: number): { readonly x: number; readonly y: number } | null;
  readonly disposed: boolean;
}

function findColumnElement(ctx: CanvasRenderingContext2D): HTMLElement | null {
  const canvas = (ctx as Partial<Pick<CanvasRenderingContext2D, 'canvas'>>).canvas;
  if (typeof HTMLElement === 'undefined' || !(canvas instanceof HTMLElement)) return null;
  return canvas.closest<HTMLElement>('[data-tl-col]');
}

function canvasOf(ctx: CanvasRenderingContext2D): HTMLCanvasElement | null {
  const canvas = (ctx as Partial<Pick<CanvasRenderingContext2D, 'canvas'>>).canvas;
  return typeof HTMLCanvasElement !== 'undefined' && canvas instanceof HTMLCanvasElement ? canvas : null;
}

export function createMotionColumnView(deps: MotionColumnDeps): MotionColumnView {
  let width = 0;
  let lastAxis: TimeAxis | null = null;
  let el: HTMLElement | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let disposed = false;

  const sceneNow = (): MotionColumnScene | null => (lastAxis === null ? null : deps.scene(lastAxis, width));

  /**
   * First draw: find the host's element and decorate it; content draws re-decorate it (labels change). The canvas
   * kept for client coordinates is the visible one the overlay is drawn on (the host draws static and content into
   * hidden strips).
   */
  const decorate = (ctx: CanvasRenderingContext2D, refresh: boolean, visible: boolean): void => {
    if (visible) canvas = canvasOf(ctx);
    if (el !== null) {
      if (refresh) deps.decorateElement?.(el);
      return;
    }
    el = findColumnElement(ctx);
    if (el !== null) deps.decorateElement?.(el);
  };

  const view: MotionColumnView = {
    id: deps.id,
    get width() {
      return width;
    },
    get axis() {
      return lastAxis;
    },
    get disposed() {
      return disposed;
    },
    scene: sceneNow,
    lanes() {
      const s = sceneNow();
      return s === null ? [] : laneRects(s, width);
    },
    hit(x, y) {
      const s = sceneNow();
      return s === null || lastAxis === null ? null : hitTestColumn(s, width, lastAxis, x, y);
    },
    element: () => el,
    toClient(x, y) {
      if (canvas === null) return null;
      const r = canvas.getBoundingClientRect();
      return { x: r.left + x, y: r.top + y };
    },
    layout(w) {
      width = w;
    },
    scrollCache: true,

    contentAffected(dirty: DirtyScopes, _ops: readonly Op[] | undefined): boolean {
      // Keys and the sampled curve of ONE line's own tracks (UX-029): note edits cannot move these pixels, and
      // another line's edits cannot either (a line's channel values and floor are its own). Conservative: any
      // track or line-record edit of this line redraws, whatever the property, and unknown ids redraw always.
      const line = lineOfMotionId(deps.id);
      if (line === null) return true;
      return dirty.lineMotion.has(line) || dirty.lineProps.has(line);
    },
    draw(layer: TimelineLayer, ctx, axis, widthPx) {
      // Static and content may be drawn for a strip taller than the view (scroll cache): that axis is never the one
      // hit-tests and gestures use.
      if (!isStripAxis(axis)) lastAxis = axis;
      width = widthPx;
      decorate(ctx, layer === 'content', !isStripAxis(axis));
      const scene = deps.scene(axis, widthPx);
      const theme = themeFor(ctx, axis);
      if (layer === 'static') {
        const snap = axisContext(axis)?.view.snapDiv ?? 4;
        drawMotionStatic(ctx, axis, widthPx, scene, theme, snap);
      } else if (layer === 'content') {
        drawMotionContent(ctx, axis, widthPx, scene, theme);
      } else {
        const overlay = deps.overlay(axis, widthPx);
        if (overlay !== null) drawMotionOverlay(ctx, axis, widthPx, scene, overlay, theme);
      }
    },
    drawFixed(ctx, axis, widthPx) {
      lastAxis = axis;
      width = widthPx;
      drawMotionFixed(ctx, axis, widthPx, deps.scene(axis, widthPx), themeFor(ctx, axis));
    },
    hitTest(x, y, axis): TimelineHit | null {
      lastAxis = axis;
      return hitTestColumn(deps.scene(axis, width), width, axis, x, y);
    },
    itemsInRect(beat0, beat1, x0, x1, axis): TimelineRectItems {
      lastAxis = axis;
      return { keys: keysInRect(deps.scene(axis, width), width, beat0, beat1, x0, x1) };
    },
    onPointerDown(e, hit) {
      deps.handlers.pointerDown?.(view, e, (hit as MotionHit | null) ?? null);
    },
    onPointerMove(e) {
      deps.handlers.pointerMove?.(view, e);
    },
    onPointerUp(e) {
      deps.handlers.pointerUp?.(view, e);
    },
    onDoubleClick(e, hit) {
      deps.handlers.doubleClick?.(view, e, (hit as MotionHit | null) ?? null);
    },
    onContextMenu(e, hit) {
      deps.handlers.contextMenu?.(view, e, (hit as MotionHit | null) ?? null, view.toClient(e.x, e.y));
    },
    onWheel(e, axis) {
      lastAxis = axis;
      return deps.handlers.wheel?.(view, e, axis) ?? false;
    },
    zoomX(factor, x) {
      deps.handlers.zoomX?.(view, factor, x);
    },
    panX(dxPx) {
      deps.handlers.panX?.(view, dxPx);
    },
    onPointerLeave() {
      deps.handlers.pointerLeave?.(view);
    },
    onPointerCancel() {
      deps.handlers.pointerCancel?.(view);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      deps.handlers.disposed?.(view);
    },
  };
  return view;
}
